import {FC, useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
//mui
import {Box, Typography} from '@mui/material';
//icons
import {ReactComponent as LogoIcon} from '@/assets/icons/logo-title.svg';
import {ReactComponent as ArrowLeftIcon} from '@/assets/icons/arrow-line-left.svg';
import {ReactComponent as ArrowUpIcon} from '@/assets/icons/arrow-line-up.svg';
//constants
import {BACK, BODY2, H4, SMOOTH, SUBTITLE1, TEXT} from '@/utils/constants';
//components
import Button from '@/shared/components/Button';
//styles
import styles from './styles.module.scss';

const SCROLL_TOP_THRESHOLD = 400;

const TermsAndConditions: FC = () => {
  const navigate = useNavigate();

  const listRef = useRef<HTMLDivElement>(null);

  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const handleBackButtonClick = () => navigate(-1);
  const handleUpButtonClick = () => listRef.current?.scrollIntoView({behavior: SMOOTH});
  const handleAnchorClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({behavior: SMOOTH});
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolledDown(window.scrollY > SCROLL_TOP_THRESHOLD);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box className={styles.wrapper} ref={listRef}>
      <Box className={styles.title}>
        <LogoIcon className={styles.title__logo} />

        <Typography variant={H4} className={styles.title__header}>
          Terms & Conditions
        </Typography>

        <Typography variant={BODY2} className={styles.title__description}>
          Effective Date: April 27, 2026
        </Typography>

        <Button
          title={BACK}
          className={styles.button__back}
          startIcon={<ArrowLeftIcon />}
          onClick={handleBackButtonClick}
        />
      </Box>

      <Box className={styles.content}>
        <Box className={styles.content__info}>
          <Typography variant={SUBTITLE1} className={styles.content__info__title}>
            The short version
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            These Terms & Conditions are the agreement between you and Event Simpli LLC for using our platform.
            We&apos;ve kept the language as plain as we can while still being precise. If you only read one section,
            read this one.
            <br />
            <br />
            • Event Simpli is software that nonprofits and other organizations use to run fundraising events (tickets,
            silent auctions, donations, registration). If you sign up an organization, you agree to these Terms on its
            behalf and are responsible for everything that happens under your account.
            <br />
            • If you are a donor or attendee, you agree to these Terms by using an event page. The organization running
            the event is responsible for the event itself, what it sells, and how it uses your information.
            <br />
            • Payments go through Stripe, PayPal, or Square. You also agree to their terms when you transact.
            <br />
            • Subscription fees are non-refundable except as noted in Section 9. Donations are generally final. Tickets
            and auction wins follow the organization&apos;s refund policy and the law.
            <br />• These Terms are governed by Texas law. Disputes are resolved by individual arbitration unless you
            opt out within 30 days (Section 17).
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title}>
            Privacy Summary
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-1">
            1. The agreement
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            These Terms & Conditions (“Terms”) are a binding agreement between Event Simpli LLC (“Event Simpli,” “we,”
            “us,” “our”), a Texas limited liability company, and you. They cover the eventsimpli.com website, our
            application, the event pages we host, and anything else that links to these Terms (together, the “Service”).
            <br />
            <br />
            By creating an account, accessing, browsing, donating, bidding, purchasing, or otherwise using any part of
            the Service, you agree to these Terms and to our Privacy Policy. If you are agreeing on behalf of an
            organization, you confirm that you have authority to do so, and “you” means that organization.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-2">
            2. Who can use the Service
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            You can use the Service only if you can form a legally binding contract with us and are not prohibited from
            doing so under U.S. or any other applicable law. You must be at least 18 (or the age of majority where you
            live). Anyone under 13 is not allowed to use the Service. Anyone between 13 and 18 may use it only with a
            parent or guardian&apos;s involvement.
            <br />
            <br />
            You may not use the Service if you appear on a U.S. government list of restricted or prohibited persons or
            are located in a country subject to comprehensive U.S. sanctions.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-3">
            3. Accounts and security
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            To use most features, an account holder must register an organization account. Keep your information
            accurate, current, and complete. You are responsible for protecting your credentials (passwords, API keys,
            MFA factors) and for everything that happens under your account. Notify us promptly at
            support@eventsimpli.com if you suspect unauthorized access. Account holders are responsible for the actions
            of their authorized users — staff, volunteers, board members, and contractors.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-4">
            4. The Service we provide
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Subject to these Terms and your timely payment of any subscription fees, we grant you a limited,
            non-exclusive, non-transferable, revocable license to use the Service for your internal business or
            charitable purposes during your subscription term. We retain all other rights.
            <br />
            <br />
            We may modify, update, or discontinue parts of the Service. We won&apos;t materially reduce core
            functionality of a paid plan during a paid term without notice.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-5">
            5. Roles: organizations vs. donors and attendees
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            5.1 Account holders (organizations) You are responsible for the legality and accuracy of your event content,
            your fundraising activities, your communications, and the data you submit. That includes complying with the
            laws of every jurisdiction where you operate — charitable solicitation registration, raffle and gaming laws,
            tax-receipting rules, anti-spam laws (CAN-SPAM, CASL, the EU/UK ePrivacy rules), telemarketing laws (TCPA,
            A2P 10DLC), and privacy and data protection laws.
            <br />
            <br />
            For donor and attendee personal information collected through the Service, you are the data controller (or
            “business”). We process it on your behalf as your service provider or processor, as set out in our Privacy
            Policy and our Data Processing Addendum (available on request).
            <br />
            <br />
            5.2 Donors and attendees (end users) If you are an end user on an organizations event page, the organization
            is responsible for the event, the goods and services offered, the use of your information, and any tax
            acknowledgments. We are responsible for operating the Service. For refund, donation, or event questions,
            contact the organization first; we will help route your request if needed.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-6">
            6. Your content and data
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            You own your content and data (“Customer Data”). You grant us a worldwide, non-exclusive, royalty-free
            license to host, copy, transmit, display, and process Customer Data only to provide and secure the Service,
            perform our obligations, comply with law, and create de-identified or aggregated data that can&apos;t be
            used to identify any individual.
            <br />
            <br />
            You confirm that you have all the rights and consents needed to give us Customer Data and to authorize our
            use of it as described here and in the Privacy Policy. You are responsible for the accuracy of Customer Data
            and for keeping back-ups of anything you want to retain after termination.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-7">
            7. Acceptable use
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Use of the Service must be lawful, honest, and consistent with these rules. You will not (and you will not
            let anyone else):
            <br />
            <br />
            • Use the Service for any illegal, fraudulent, deceptive, or harmful purpose, including sham fundraising or
            misrepresentation of charitable purpose.
            <br />
            • Send communications that violate CAN-SPAM, CASL, the TCPA, A2P 10DLC, or any similar rule. Marketing email
            and SMS require valid consent, working opt-outs, and required disclosures.
            <br />
            • Run raffles, sweepstakes, lotteries, or gaming except where permitted by law and with all required
            registrations and disclosures.
            <br />
            • Upload or transmit content that infringes intellectual property, defames anyone, is sexually explicit
            involving minors, harasses or threatens others, or contains malware.
            <br />
            • Reverse engineer, decompile, or scrape the Service except as expressly permitted by law.
            <br />
            • Resell, rent, lease, or sublicense the Service except as expressly authorized.
            <br />
            • Build a competing product using the Service, or benchmark it for publication without our written consent.
            <br />
            • Interfere with the Service&apos;s security, integrity, or performance, including exceeding documented rate
            limits or bypassing access controls.
            <br />
            • Upload data subject to laws or regulations we don&apos;t expressly support — including HIPAA-protected
            health information, payment card data outside the supported processor flows, sensitive children&apos;s data,
            or U.S. government classified information.
            <br />
            <br />
            If you see something that looks like abuse — fraud, suspicious messaging, unauthorized access, or a security
            vulnerability — please tell us at abuse@eventsimpli.com.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-8">
            8. Auctions, tickets, and donations
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            The Service lets organizations offer tickets, run silent and live auctions, and accept donations. We are the
            software provider, not the seller of any auction item, the host of any event, the producer of any ticketed
            performance, the organizer of any sweepstakes or raffle, or the recipient of any donation.
            <br />
            <br />
            These auction-specific terms apply whenever an organization runs an auction through the Service:
            <br />
            <br />
            1. Bidders must be 18 or older (or the age of majority where they live), unless the organization sets a
            stricter requirement.
            <br />
            2. All bids are binding when placed. The organization may withdraw an obviously erroneous bid before the
            close of the auction at its discretion.
            <br />
            3. Bids must be in U.S. dollars (or the currency the organization specifies) and must be received by the
            time the organization sets.
            <br />
            4. The winning bid for a given item is the highest bid received before the close. If two bids match, the
            earlier-received bid wins.
            <br />
            5. Successful bidders are notified by the organization, typically within seven days of the close.
            <br />
            6. Bids below a reserve price, late bids, or incomplete bids will not be accepted.
            <br />
            7. Successful bidders pay the organization directly through the payment options offered in the Service.
            <br />
            8. If the organization can&apos;t reach a winning bidder within seven days, or hasn&apos;t received payment
            within twenty-one days after notification, the organization may award the item to the next-highest bidder.
            <br />
            9. Items are subject to availability and to any restrictions the organization discloses. Lots are not
            transferable and there are generally no cash alternatives.
            <br />
            10. Item descriptions are provided by the organization. We do not verify them. Bidders are responsible for
            checking item details with the organization before bidding.
            <br />
            11. The organization may change, alter, or remove a description or item from the auction at any time before
            close.
            <br />
            12. If circumstances beyond the organization&apos;s control make an auction impossible, the organization
            may, in its discretion, vary or cancel the auction without liability.
            <br />
            13. The organization will run the auction in a fair and consistent manner; on questions of bid validity, the
            organization&apos;s decision is final. It is the bidder&apos;s responsibility to confirm that a bid was
            received before the auction closes.
            <br />
            14. Sales taxes, shipping costs, and fair-market-value disclosures (for tax-receipting purposes) are the
            organization&apos;s responsibility.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-9">
            9. Refunds, subscriptions, and donations
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            9.1 Subscription fees you pay us Subscription fees are billed in advance and are non-refundable except:
            <br />
            <br />• Annual plans — within 14 days of initial purchase if you haven&apos;t actively used the Service.
            Email support@eventsimpli.com with your account email and order reference within the 14-day window.
            <br />• Where required by applicable law.
            <br />
            <br />
            Cancellation stops future renewals but does not refund the current period. Fees are non-refundable if we
            terminate for breach. Service-level credits, if we publish them for your plan, are your sole remedy for
            failures to meet the commitment. Past-due amounts accrue interest at 1.0% per month or the legal maximum,
            whichever is less.
            <br />
            <br />
            9.2 Tickets Tickets are sold by the organization, not by us. Refund eligibility is determined by the
            organization&apos;s published policy and applicable law. If an event is cancelled or materially rescheduled,
            the organization is responsible for refunding in accordance with the law. Processing fees paid to the
            payment processor are not retained by the organization or by us and may not be refundable unless the
            underlying charge is fully reversed. Contact the organization for refund requests.
            <br />
            <br />
            9.3 Auction wins Auction items are offered and fulfilled by the organization. Auction wins are generally
            final. The organization handles returns or refunds for items materially not as described or undeliverable,
            in line with its policy and the law.
            <br />
            <br />
            9.4 Donations D onations are charitable contributions to the organization, not to us. They are generally
            final and non-refundable. If you made a clearly mistaken or unauthorized donation, contact the organization
            promptly — most will refund clearly erroneous transactions in good faith, subject to tax-receipting
            consequences. Tax acknowledgments are issued by the organization; donors should keep their own records and
            consult a tax advisor. Recurring donations can be cancelled at any time using the link in your receipt;
            cancellation stops future charges but does not refund prior ones.
            <br />
            <br />
            9.5 Chargebacks Please contact the organization (for ticket, auction, or donation transactions) or us (for
            subscription fees) before initiating a chargeback. Chargebacks initiated without an attempt to resolve the
            dispute may be contested. Nothing here limits your non-waivable consumer-protection rights.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-10">
            10. Payment processing
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Card and electronic payments are processed by Stripe, PayPal, or Square. When you transact through the
            Service, you also agree to the applicable processor&apos;s terms and privacy policy. Processors apply their
            own fees, holds, payout schedules, and refund procedures. We do not store full card numbers; cardholder data
            is collected and held by the processor&apos;s PCI-compliant infrastructure.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-11">
            11. Communications
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            By providing an email address or phone number, you agree to receive transactional communications related to
            your account or the event you registered for, including by email and SMS. Standard message and data rates
            may apply. Unsubscribe from marketing emails using the link in any such message; opt out of SMS by replying
            STOP. Organizations must obtain all required consents from end users before sending marketing email or SMS
            through the Service and must provide working opt-out mechanisms. Our Mobile Terms describe SMS in more
            detail.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-12">
            12. Intellectual property
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We own the Service and all related software, design, content, and brand. Your limited license to use the
            Service is set out in Section 4. We don&apos;t grant any other rights. If you send us suggestions or
            feedback, we may use them freely without obligation to you.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-13">
            13. Third-party services
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            The Service interoperates with third-party services (payment processors, email and SMS providers, analytics,
            and integrations you enable). We do not control them, and their use is governed by their own terms and
            privacy policies.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-14">
            14. Term, suspension, and termination
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            These Terms are in effect while you use the Service. Either party may terminate for material breach if the
            breach isn&apos;t cured within 30 days after written notice (or immediately for breaches of confidentiality,
            IP, or these Sections 6, 7, or 12).
            <br />
            <br />
            We may suspend or limit access immediately if (a) your use poses a security, legal, or reputational risk;
            (b) you fail to pay; (c) we are required to by law; or (d) a payment processor instructs us. On termination,
            your right to use the Service ends, we will make a reasonable effort to provide an export of your Customer
            Data on request within 30 days, and we may delete remaining Customer Data in the ordinary course.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-15">
            15. Warranty disclaimer
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            The Service is provided “as is” and “as available,” without warranties of any kind, whether express,
            implied, statutory, or otherwise. To the extent permitted by law, we disclaim implied warranties of
            merchantability, fitness for a particular purpose, title, and non-infringement. We do not guarantee that the
            Service will be uninterrupted, error-free, or secure, or that any transaction will be transmitted
            successfully. You are responsible for confirming transactions.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-16">
            16. Limitation of liability
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            To the extent permitted by law, neither party will be liable for indirect, incidental, special,
            consequential, exemplary, or punitive damages, or for loss of profits, revenue, business, goodwill,
            donations, data, or use, arising out of these Terms or the Service. Our total liability arising out of these
            Terms or the Service will not exceed the greater of (a) the total fees you paid us in the 12 months before
            the event giving rise to the liability or (b) one hundred U.S. dollars ($100). These limits apply even if a
            limited remedy fails its essential purpose. Nothing here limits liability for fraud, willful misconduct, or
            any liability that can&apos;t be excluded or limit be limited under applicable law.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-17">
            17. Governing law and disputes
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            These Terms are governed by Texas law, without regard to conflict-of-laws principles. The UN Convention on
            Contracts for the International Sale of Goods does not apply.
            <br />
            <br />
            Informal resolution. Before filing any claim, send a notice to legal@eventsimpli.com describing the dispute
            and the relief you want. The parties will try to resolve it in good faith for at least 60 days before
            starting arbitration.
            <br />
            <br />
            Arbitration. Except for matters carved out below, disputes will be resolved by final and binding individual
            arbitration administered by the American Arbitration Association under its Consumer or Commercial
            Arbitration Rules (whichever applies). The arbitration will be conducted by a single arbitrator in English,
            in Travis County, Texas or, at your election as a consumer, by telephone, video, or document submission.
            Judgment may be entered in any court of competent jurisdiction.
            <br />
            <br />
            Class-action waiver. Disputes will be brought only in your individual capacity, not as part of any class,
            collective, or representative proceeding.
            <br />
            <br />
            Carve-outs. Either party may bring an individual action in small-claims court, seek injunctive relief in
            court to address actual or threatened violations of intellectual property or confidentiality rights, and
            pursue claims that cannot legally be arbitrated.
            <br />
            <br />
            30-day opt-out. You may opt out of this Section 17 by emailing legal@eventsimpli.com within 30 days of first
            accepting these Terms with your name, account email, and the words “arbitration opt-out.” For matters not
            subject to arbitration, the parties consent to the exclusive jurisdiction of the state and federal courts in
            Travis County, Texas.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-18">
            18. Indemnification
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            You will defend, indemnify, and hold us and our officers, directors, employees, and agents harmless from
            third-party claims, damages, losses, and reasonable attorneys&apos; fees arising out of: your use of the
            Service in breach of these Terms or applicable law; Customer Data (including IP infringement or unlawful
            content); your fundraising activities, events, auction items, raffles, sweepstakes, or donor receipts; a
            dispute between you and an end user, donor, attendee, or other third party; or your willful misconduct or
            gross negligence. We&apos;ll give you prompt notice of any claim, let you control the defense (you
            can&apos;t settle in a way that binds us without our written consent), and cooperate at your expense.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-19">
            19. Changes to the Service or Terms
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We may update these Terms from time to time. For material changes, we&apos;ll give at least 14 days&apos;
            notice through the Service or by email to your account address before they take effect (except where the law
            requires faster action). Continued use after the change takes effect means you accept the revised Terms.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-20">
            20. Other terms
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Force majeure. Neither party is liable for failures or delays (other than payment) caused by events beyond
            reasonable control, including natural disasters, pandemics, war, civil disorder, labor disputes,
            governmental action, and internet outages.
            <br />
            <br />
            Notices. We may give you notices in the Service or to the email address on your account. You may send legal
            notices to legal@eventsimpli.com and the address below.
            <br />
            <br />
            Entire agreement. These Terms, with the Privacy Policy, the Mobile Terms, any DPA, and any order, are the
            entire agreement for the Service and supersede prior agreements on the same subject.
            <br />
            <br />
            Assignment. You may not assign these Terms without our written consent; we may assign them in connection
            with a merger, acquisition, financing, or sale of substantially all assets.
            <br />
            <br />
            Severability. If any provision is held unenforceable, the rest remain in effect.
            <br />
            <br />
            No waiver. Failure to enforce a provision is not a waiver of future enforcement.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-21">
            21. Contact
          </Typography>
          <Typography variant={BODY2} className={styles.content__info__description}>
            General support: support@eventsimpli.com
            <br />
            <br />
            Legal notices: legal@eventsimpli.com
            <br />
            <br />
            Abuse and Trust & Safety: abuse@eventsimpli.com
            <br />
            <br />
            Event Simpli LLC
          </Typography>
        </Box>

        <Box className={styles.content__menu}>
          <Typography variant={SUBTITLE1} className={styles.content__menu__title}>
            Table of contents
          </Typography>

          <ul className={styles.content__menu__list}>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-1" onClick={handleAnchorClick('section-1')}>
                The agreement.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-2" onClick={handleAnchorClick('section-2')}>
                Who can use the Service.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-3" onClick={handleAnchorClick('section-3')}>
                Accounts and security.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-4" onClick={handleAnchorClick('section-4')}>
                The service we provide.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-5" onClick={handleAnchorClick('section-5')}>
                Roles: organizations vs. donors and attendees.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-6" onClick={handleAnchorClick('section-6')}>
                Your content and data.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-7" onClick={handleAnchorClick('section-7')}>
                Acceptable use.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-8" onClick={handleAnchorClick('section-8')}>
                Auctions, tickets, and donations.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-9" onClick={handleAnchorClick('section-9')}>
                Refunds, subscriptions, and donations.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-10" onClick={handleAnchorClick('section-10')}>
                Payment processing.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-11" onClick={handleAnchorClick('section-11')}>
                Communications.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-12" onClick={handleAnchorClick('section-12')}>
                Intellectual property.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-13" onClick={handleAnchorClick('section-13')}>
                Third-party services.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-14" onClick={handleAnchorClick('section-14')}>
                Term, suspension, and termination.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-15" onClick={handleAnchorClick('section-15')}>
                Warranty disclaimer.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-16" onClick={handleAnchorClick('section-16')}>
                Limitation of liability.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-17" onClick={handleAnchorClick('section-17')}>
                Governing law and disputes.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-18" onClick={handleAnchorClick('section-18')}>
                Indemnification.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-19" onClick={handleAnchorClick('section-19')}>
                Changes to the Service or Terms.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-20" onClick={handleAnchorClick('section-20')}>
                Other terms.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-21" onClick={handleAnchorClick('section-21')}>
                Contact.
              </Link>
            </li>
          </ul>

          {isScrolledDown && (
            <Button
              title="Back to top"
              variant={TEXT}
              disableRipple
              className={styles.button__top}
              endIcon={<ArrowUpIcon />}
              onClick={handleUpButtonClick}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
